<template>
  <div
    class="modern-color-theme font-poppins"
    :class="{
      'w-full': props.wide,
      'w-min': !props.wide,
      'relative': props.disabled === 'loading'
    }"
    v-bind="containerProps"
    data-component-name="VButton"
  >
    <VTooltip :content="props.title" :position="titlePosition">
      <component
        :is="props.href ? 'a' : 'button'"
        ref="button-ref"
        v-bind="$attrs"
        :type="props.href ? 'undefined' : 'button'"
        :href="props.disabled ? undefined : props.href"
        :target="props.href && props.newTab ? '_blank' : undefined"
        class="
          w-full flex items-center gap-2
          overflow-ellipsis overflow-hidden whitespace-nowrap h-min
        "
        :class="[computedClass, theme.button]"
        :tabindex="props.tabindex"
        :disabled="!!props.disabled"
        :aria-label="ariaLabel"
      >
        <slot name="prefix" />
        <slot />
        <slot name="suffix" />
      </component>
    </VTooltip>
    <div v-if="props.disabled === 'loading'" class="absolute inset-0 flex items-center justify-center pointer-events-none">
      <VSpinner :size="props.size" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import type { Size, V } from '@utils/types'
import VSpinner from '../labels/VSpinner.vue'
import { globalLocalize } from '@utils/localization'
import VTooltip from '@library/dialogs/VTooltip.vue';
import type { Position } from '@utils/position';
import type { ThemeProp } from '@library/themes/types';
import { useTheme } from '@library/themes/themes';

defineOptions({
  name: 'VButton',
  inheritAttrs: false
})

const props = withDefaults(
  defineProps<{
    variant?: V.Button.Variant
    color?: V.Button.Color
    wide?: boolean
    disabled?: boolean | 'loading'
    size?: Extract<Size, 'xl' | 'lg' | 'base' | 'sm' | 'xs'>
    tabindex?: number | string
    title?: string
    titlePosition?: Position.Snap
    icon?: boolean
    round?: boolean
    containerProps?: Record<string, unknown>
    href?: string
    raw?: boolean
    newTab?: boolean
    alt?: string
    align?: 'left' | 'center' | 'right'
    theme?: ThemeProp<'VButton'>
  }>(),
  {
    variant: 'contained',
    color: 'primary',
    size: 'base',
    wide: false,
    tabindex: undefined,
    title: undefined,
    icon: false,
    round: false,
    disabled: false,
    containerProps: () => Object.create(null) as Record<string, unknown>,
    href: undefined,
    raw: false,
    alt: undefined,
    newTab: false,
    align: 'center',
    titlePosition: 'bottom left',
    theme: undefined
  }
)

const theme = useTheme(props, 'VButton')

const computedClass = computed(() => {
  if (props.raw) return []

  const classes = []

  if (props.variant !== 'text') {
    classes.push('border')
  }

  if (props.round) {
    classes.push('!rounded-full')
  }

  switch(props.align){
    case 'left':
      classes.push('justify-start')
      break
    case 'right':
      classes.push('justify-end')
      break
    default:
      classes.push('justify-around')
      break
  }

  classes.push(theme.value.colors[`${props.variant}/${props.color}`])
  classes.push(theme.value.sizes[`${props.icon ? 'icon_' : props.variant === 'text' ? 'text_' : ''}${props.size}`])

  return classes
})

const ariaLabel = computed(() => {
  if (props.disabled === 'loading') {
    const prefix = props.alt ? `${props.alt} ` : ''
    return `${prefix}${globalLocalize('component-library.buttons.loading')}`
  }
  
  return props.alt
})

const buttonRef = useTemplateRef<HTMLElement>('button-ref')

defineExpose({
  focus () {
    buttonRef.value?.focus()
  }
})
</script>

<style lang="scss" scoped>
  [data-component-name="VButtonGroup"] {
    & [data-component-name="VButton"] {
      button, a {
        border-radius: 0;
        border: none;
      }
    }

    & [data-component-name="VButton"]:first-child {
      button, a {
        border-radius: 0.375rem 0 0 0.375rem;
      }
    }

    & [data-component-name="VButton"]:last-child {
      button, a {
        border-radius: 0 0.375rem 0.375rem 0;
      }
    }
  }
</style>